import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Breadcrumb from "@/components/ui/breadcrumb";
import Button from "@/components/ui/button";
import Chip from "@/components/ui/chip";
import ConfirmModal from "@/components/ui/confirm-modal";
import { LeftChevron, Close, Check } from "@/components/icons";
import type { FilterField, TableColumn } from "@/types/component";
import type {
  PersonnelRequestEntry,
  PersonnelRequestStatus,
} from "../types/request-and-approval.types";
import { requestAndApprovalApi } from "../services/request-and-approval.api";
import styles from "./EducationOfficerPage.module.scss";
import AddPersonModal from "../components/AddPersonModal";
import VirtualClassroomsListModal from "../components/VirtualClassroomsListModal";
import { CardView } from "@/components/icons";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import { UserRoleType } from "@/features/roles-and-permissions/types/roles-and-permissions.types";
import { useStore } from "@/store";
import { educationManagementApi } from "@/features/education-management/services/education-management.api";
import { EDUCATION_TYPE_API } from "@/features/education-management/types/education-management.types";

const EducationOfficerPage: React.FC = () => {
  const { t } = useTranslation();
  const STATUS_LABELS: Record<PersonnelRequestStatus, string> = {
    onaylandi: t("educationOfficer.statusLabels.onaylandi"),
    ilk_onay_bekliyor: t("educationOfficer.statusLabels.ilk_onay_bekliyor"),
    ikinci_onay_bekliyor: t("educationOfficer.statusLabels.ikinci_onay_bekliyor"),
    ucuncu_onay_bekliyor: t("educationOfficer.statusLabels.ucuncu_onay_bekliyor"),
    reddedildi: t("educationOfficer.statusLabels.reddedildi"),
    iptal_edildi: t("educationOfficer.statusLabels.iptal_edildi"),
    tamamlandi: t("educationOfficer.statusLabels.tamamlandi"),
  };
  const navigate = useNavigate();
  const { educationId } = useParams<{ educationId: string }>();
  const { hasPermission } = usePermission();
  const canAddPerson = hasPermission(PERMISSIONS.REQUEST_AND_APPROVAL_CREATE);
  const userType = useStore((s) => s.userType);
  const location = useLocation();
  const educationName =
    (location.state as { educationName?: string } | null)?.educationName ||
    t("educationOfficer.defaultEducationName");

  const [personnel, setPersonnel] = useState<PersonnelRequestEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRows, setSelectedRows] = useState<PersonnelRequestEntry[]>([]);
  const [filterValues, setFilterValues] = useState<
    Record<string, PersonnelRequestStatus | PersonnelRequestStatus[]>
  >({});
  const [isConfirmSendOpen, setIsConfirmSendOpen] = useState(false);
  const [isConfirmRejectOpen, setIsConfirmRejectOpen] = useState(false);
  const [isSendLoading, setIsSendLoading] = useState(false);
  const [isAddPersonModalOpen, setIsAddPersonModalOpen] = useState(false);
  const [isVirtualClassroomsModalOpen, setIsVirtualClassroomsModalOpen] =
    useState(false);
  const [educationType, setEducationType] = useState<number | null>(null);

  useEffect(() => {
    if (!educationId) return;
    educationManagementApi
      .getById(educationId)
      .then(({ data }) => setEducationType(data.type))
      .catch(() => setEducationType(null));
  }, [educationId]);

  // Sanal sınıf sadece Yüz Yüze (ON_SITE) ve Canlı (ONLINE) eğitimlerde anlamlı.
  // Üniteli ve Ünitesiz eğitimlerde sınıf kavramı olmadığı için butonu gizle.
  const isVirtualClassroomApplicable =
    educationType === EDUCATION_TYPE_API.ON_SITE ||
    educationType === EDUCATION_TYPE_API.ONLINE;

  const fetchPersonnel = useCallback(async () => {
    if (!educationId) return;
    setIsLoading(true);
    try {
      const response = await requestAndApprovalApi.getPersonnelByEducationId(
        educationId,
        {
          search: searchTerm,
          status: filterValues.status,
          page: currentPage,
          pageSize,
        },
      );
      setPersonnel(response.data);
      setTotalRecords(response.total);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching personnel:", error);
    } finally {
      setIsLoading(false);
    }
  }, [educationId, searchTerm, filterValues, currentPage, pageSize]);

  useEffect(() => {
    fetchPersonnel();
  }, [fetchPersonnel]);

  const handleBack = () => {
    navigate("/dashboard/request-and-approval");
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filters: Record<string, unknown>) => {
    setFilterValues(
      filters as Record<
        string,
        PersonnelRequestStatus | PersonnelRequestStatus[]
      >,
    );
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleRowSelect = (row: PersonnelRequestEntry) => {
    setSelectedRows((prev) => {
      const isSelected = prev.some((r) => r.id === row.id);
      if (isSelected) return prev.filter((r) => r.id !== row.id);
      return [...prev, row];
    });
  };

  // Kümülatif onay yetkisi: N. onaycı, 1..N seviyelerinde bekleyen enrollment'ları
  // işleyebilir. 2. onaycı hem 1. hem 2. bekleyenleri; 3. onaycı hem 1., 2. hem de 3.
  // bekleyenleri seçebilir. 1. onaycı yalnızca 1. bekleyenleri işleyebilir.
  const selectableStatuses: PersonnelRequestStatus[] =
    userType === UserRoleType.EDUCATION_SUPERVISOR
      ? ["ilk_onay_bekliyor"]
      : userType === UserRoleType.BRANCH_MANAGER
        ? ["ilk_onay_bekliyor", "ikinci_onay_bekliyor"]
        : userType === UserRoleType.CENTRAL_MANAGER ||
            userType === UserRoleType.EDUCATION_MANAGER
          ? [
              "ilk_onay_bekliyor",
              "ikinci_onay_bekliyor",
              "ucuncu_onay_bekliyor",
            ]
          : [];

  const handleSelectAll = (checked: boolean) => {
    setSelectedRows(
      checked ? personnel.filter((p) => selectableStatuses.includes(p.status)) : [],
    );
  };

  const removeFromCart = (id: string) => {
    setSelectedRows((prev) => prev.filter((r) => r.id !== id));
  };

  const handleAddPerson = () => {
    if (!educationId) return;
    setIsAddPersonModalOpen(true);
  };

  const handleClickSendForApproval = () => {
    setIsConfirmSendOpen(true);
  };
  const handleClickReject = () => {
    setIsConfirmRejectOpen(true);
  };

  const handleConfirmSendForApproval = async () => {
    if (selectedRows.length === 0) return;
    setIsSendLoading(true);
    try {
      await requestAndApprovalApi.bulkReviewEnrollments({
        enrollmentIds: selectedRows.map((r) => r.id),
        isApproved: true,
      });
      setIsConfirmSendOpen(false);
      setSelectedRows([]);
      fetchPersonnel();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error sending for approval:", error);
    } finally {
      setIsSendLoading(false);
    }
  };

  const handleConfirmReject = async () => {
    if (selectedRows.length === 0) return;
    setIsSendLoading(true);
    try {
      await requestAndApprovalApi.bulkReviewEnrollments({
        enrollmentIds: selectedRows.map((r) => r.id),
        isApproved: false,
      });
      setIsConfirmRejectOpen(false);
      setSelectedRows([]);
      fetchPersonnel();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error rejecting requests:", error);
    } finally {
      setIsSendLoading(false);
    }
  };

  const getStatusChipType = (
    status: PersonnelRequestStatus,
  ): "success" | "warning" | "error" | "default" => {
    switch (status) {
      case "onaylandi":
      case "tamamlandi":
        return "success";
      case "reddedildi":
        return "error";
      case "iptal_edildi":
        return "default";
      case "ilk_onay_bekliyor":
        return "warning";
      case "ucuncu_onay_bekliyor":
        return "success";
      case "ikinci_onay_bekliyor":
      default:
        return "warning";
    }
  };

  const filterFields: FilterField[] = [
    {
      name: "status",
      label: t("educationOfficer.filters.status"),
      type: "dropdown",
      placeholder: t("educationOfficer.filters.statusPlaceholder"),
      options: Object.entries(STATUS_LABELS).map(([value, label]) => ({
        value,
        label,
      })),
    },
  ];

  const breadcrumbItems = [
    {
      label: t("educationOfficer.breadcrumb.requestAndApproval"),
      onClick: handleBack,
    },
    {
      label: `${educationName} ${t("educationOfficer.breadcrumb.requestsSuffix")}`,
    },
  ];

  const columns: TableColumn<PersonnelRequestEntry>[] = [
    {
      header: t("educationOfficer.columns.personnelName"),
      accessor: "personelAdi",
      className: "personnel-name-column",
    },
    {
      header: t("educationOfficer.columns.tcKimlikNo"),
      accessor: "tcKimlikNo",
      className: "tc-column",
    },
    {
      header: t("educationOfficer.columns.unvan"),
      accessor: "unvan",
      className: "unvan-column",
    },
    {
      header: t("educationOfficer.columns.birim"),
      accessor: "birim",
      className: "birim-column",
    },
    {
      header: t("educationOfficer.columns.status"),
      accessor: (row) => (
        <Chip type={getStatusChipType(row.status)} variant="soft" size="small">
          {STATUS_LABELS[row.status]}
        </Chip>
      ),
      className: "status-column",
    },
  ];

  return (
    <DashboardLayout>
      <Header title={t("requestAndApproval.title")} />

      <div className={styles.breadcrumbContainer}>
        <button
          type="button"
          className={styles.backButton}
          onClick={handleBack}
          aria-label={t("common.back")}
        >
          <LeftChevron width={20} height={20} />
        </button>
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <PageHeader
        searchPlaceholder={t("educationOfficer.searchPlaceholder")}
        searchValue={searchTerm}
        onSearch={handleSearch}
        filters={filterFields}
        onFilterChange={handleFilterChange}
        onAddNew={canAddPerson ? handleAddPerson : undefined}
        addButtonLabel={canAddPerson ? t("educationOfficer.addPerson") : undefined}
        leftExtraContent={
          educationId && isVirtualClassroomApplicable ? (
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              type="button"
              startIcon={<CardView width={18} height={18} />}
              onClick={() => setIsVirtualClassroomsModalOpen(true)}
            >
              {t("virtualClassrooms.modal.openButton")}
            </Button>
          ) : null
        }
      />

      <div className={styles.pageContent}>
        <div className={styles.mainLayout}>
          <div className={styles.tableSection}>
            <Table
              columns={columns}
              data={personnel}
              isLoading={isLoading}
              selectable
              isRowSelectable={(row) => selectableStatuses.includes(row.status)}
              selectedRows={selectedRows}
              onRowSelect={handleRowSelect}
              onSelectAll={handleSelectAll}
              getRowId={(row) => row.id}
              currentPage={currentPage}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              emptyMessage={t("educationOfficer.noData")}
            />
          </div>

          <div className={styles.approvalCartColumn}>
            <aside className={styles.approvalCart}>
              <h2 className={styles.approvalCartTitle}>
                {t("educationOfficer.approvalCart")}
              </h2>
              <div className={styles.cartList}>
                {selectedRows.length === 0 ? (
                  <p className={styles.emptyCart}>
                    {t("educationOfficer.emptyCart")}
                  </p>
                ) : (
                  selectedRows.map((row) => (
                    <div key={row.id} className={styles.cartItem}>
                      <div className={styles.cartItemContent}>
                        <span className={styles.cartItemName}>
                          {row.personelAdi}
                        </span>
                        <span className={styles.cartItemTc}>
                          {row.tcKimlikNo}
                        </span>
                      </div>
                      <button
                        type="button"
                        className={styles.cartItemRemove}
                        onClick={() => removeFromCart(row.id)}
                        aria-label={t("common.remove")}
                      >
                        <Close width={20} height={20} color="#707a8f" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </aside>
            <div className={styles.cartFooter}>
              <div className={styles.cartTotalWrap}>
                <span className={styles.cartTotal}>
                  <span className={styles.cartTotalLabel}>
                    {t("educationOfficer.total")}
                  </span>
                  <span className={styles.cartTotalCount}>
                    {selectedRows.length}
                  </span>
                </span>
              </div>
              <Button
                variant="outlined"
                color="error"
                size="medium"
                className={styles.submitButton}
                onClick={handleClickReject}
                disabled={selectedRows.length === 0}
              >
                {t("educationOfficer.reject")}
              </Button>
              <Button
                variant="contained"
                color="success"
                size="medium"
                className={styles.submitButton}
                startIcon={<Check width={20} height={20} color="#fff" />}
                onClick={handleClickSendForApproval}
                disabled={selectedRows.length === 0}
              >
                {t("educationOfficer.sendForApproval")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={isConfirmSendOpen}
        onClose={() => !isSendLoading && setIsConfirmSendOpen(false)}
        onConfirm={handleConfirmSendForApproval}
        title={t("educationOfficer.confirmSend.title")}
        message={
          <>
            {t("educationOfficer.confirmSend.messagePrefix")}{" "}
            <span className={styles.confirmModalCount}>
              {selectedRows.length} {t("educationOfficer.confirmSend.adet")}
            </span>{" "}
            {t("educationOfficer.confirmSend.messageSuffix")}
          </>
        }
        confirmText={t("educationOfficer.confirmSend.confirm")}
        cancelText={t("educationOfficer.confirmSend.cancel")}
        loading={isSendLoading}
      />
      <ConfirmModal
        isOpen={isConfirmRejectOpen}
        onClose={() => !isSendLoading && setIsConfirmRejectOpen(false)}
        onConfirm={handleConfirmReject}
        title={t("educationOfficer.confirmReject.title")}
        message={
          <>
            {t("educationOfficer.confirmReject.messagePrefix")}{" "}
            <span className={styles.confirmModalCount}>
              {selectedRows.length} {t("educationOfficer.confirmReject.adet")}
            </span>{" "}
            {t("educationOfficer.confirmReject.messageSuffix")}
          </>
        }
        confirmText={t("educationOfficer.confirmReject.confirm")}
        cancelText={t("educationOfficer.confirmReject.cancel")}
        loading={isSendLoading}
        confirmVariant="danger"
      />
      {educationId && (
        <>
          <AddPersonModal
            isOpen={isAddPersonModalOpen}
            onClose={() => setIsAddPersonModalOpen(false)}
            educationId={educationId}
            onAdded={() => {
              setIsAddPersonModalOpen(false);
              fetchPersonnel();
            }}
          />
          <VirtualClassroomsListModal
            isOpen={isVirtualClassroomsModalOpen}
            onClose={() => setIsVirtualClassroomsModalOpen(false)}
            educationId={educationId}
            subtitle={educationName}
          />
        </>
      )}
    </DashboardLayout>
  );
};

export default EducationOfficerPage;
